// src/pages/AcceptInvite.js
// Landing page for an emailed team invitation: set a name and password, join the
// organization and get signed in. No admin approval: the organization already has it.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/api';
import { useStore } from '../store';
import AuthCard, { alertStyle, fullWidthBtn, linkStyle } from '../components/AuthCard';

// Read the token once, then strip it from the address bar so it doesn't linger in
// history or leak through a Referer header.
function takeTokenFromUrl() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  if (token) window.history.replaceState(null, '', window.location.pathname);
  return token;
}

export default function AcceptInvite() {
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useStore();
  const [token] = useState(takeTokenFromUrl);
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState(token ? '' : 'This invitation link is incomplete. Open the link from your email again.');
  const [form, setForm] = useState({ name: '', password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    authAPI.invitation(token)
      .then(r => setInvite(r.data.invitation))
      .catch(err => setLoadError(err.response?.data?.error || 'Could not load this invitation.'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    setSaving(true);
    try {
      await authAPI.acceptInvitation({ token, name: form.name.trim(), password: form.password });
      await checkAuth();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not accept the invitation. Please try again.');
      setSaving(false);
    }
  };

  const footer = <Link to="/login" style={linkStyle}>Sign in instead</Link>;

  if (loadError) {
    return <AuthCard subtitle="Team invitation" footer={footer}><div style={alertStyle('error')}>{loadError}</div></AuthCard>;
  }
  if (!invite) return <AuthCard subtitle="Team invitation"><div className="loading-center"><div className="spinner" /></div></AuthCard>;

  if (invite.email_taken) {
    return (
      <AuthCard subtitle="Team invitation" footer={footer}>
        <div style={alertStyle('error')}>
          <strong>{invite.email}</strong> already has a Post Calendar account. Each account belongs to one organization, so it can't join {invite.organization_name}. Ask {invite.invited_by_name || 'the person who invited you'} to invite a different email.
        </div>
      </AuthCard>
    );
  }

  if (user) {
    return (
      <AuthCard subtitle="Team invitation">
        <div style={alertStyle('error')}>
          You're signed in as <strong>{user.email}</strong>. Sign out to accept this invitation for <strong>{invite.email}</strong>.
        </div>
        <button className="btn btn-primary" style={fullWidthBtn} onClick={() => logout()}>Sign out</button>
      </AuthCard>
    );
  }

  return (
    <AuthCard subtitle={`Join ${invite.organization_name}`} width={420} footer={footer}>
      <p style={{ fontSize: 12, color: '#4a5568', lineHeight: 1.6, marginBottom: 16, textAlign: 'center' }}>
        {invite.invited_by_name || 'Your team'} invited <strong>{invite.email}</strong> to join as {invite.role === 'admin' ? 'an admin' : 'a member'}.
      </p>
      <form onSubmit={submit}>
        {error && <div style={alertStyle('error')}>{error}</div>}
        <div className="form-group">
          <label className="form-label">Full name</label>
          <input className="form-input" maxLength={255} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <input className="form-input" type="password" autoComplete="new-password" maxLength={72} placeholder="Min 8 characters" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
        </div>
        <div className="form-group" style={{ marginBottom: 20 }}>
          <label className="form-label">Confirm password</label>
          <input className="form-input" type="password" autoComplete="new-password" maxLength={72} value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} required />
        </div>
        <p style={{ fontSize: 11, color: '#8892a4', lineHeight: 1.6, marginBottom: 12, textAlign: 'center' }}>
          By joining, you agree to our <Link to="/terms" style={{ color: '#0f766e' }}>Terms of Service</Link> and <Link to="/privacy" style={{ color: '#0f766e' }}>Privacy Policy</Link>.
        </p>
        <button type="submit" className="btn btn-primary" style={fullWidthBtn} disabled={saving || !form.name.trim()}>
          {saving ? 'Joining…' : `Join ${invite.organization_name}`}
        </button>
      </form>
    </AuthCard>
  );
}
